import { useCallback, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthProvider";
import { completeLessonAttempt } from "../api/learnApi";
import type { LessonResult } from "../lessonTypes";
import { LESSON_MESSAGES } from "../messages";

interface UseContentCompletionParams {
    userId?: number | string;
    courseId: string;
    contentId: string;
    attemptId?: number | string | null;
    alreadyCompleted: boolean; // Replays award nothing and are not sent to the server
}

// Counts what the learner did, and sends the result when the lesson or project ends
export const useContentCompletion = ({
                                         userId,
                                         courseId,
                                         contentId,
                                         attemptId,
                                         alreadyCompleted
                                     }: UseContentCompletionParams) => {
    const { refreshUser } = useAuth();

    // Refs: the numbers must be current at the moment of sending and never cause a render
    const counters = useRef({ correct: 0, mistakes: 0, hints: 0 });
    const inFlight = useRef(false); // A second click while the request runs is ignored

    const [completing, setCompleting] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState<LessonResult | null>(null);

    const recordAnswer = useCallback((isCorrect: boolean) => {
        counters.current[isCorrect ? "correct" : "mistakes"] += 1;
    }, []);

    const recordHint = useCallback(() => {
        counters.current.hints += 1;
    }, []);

    // Resolves to true when the finish screens may be shown
    const complete = useCallback(async (): Promise<boolean> => {
        if (alreadyCompleted) return true; // Nothing to save or award
        if (inFlight.current) return false;

        // Without an attempt the request would go to ".../lesson-attempt/null/complete"
        if (userId === undefined || attemptId === null || attemptId === undefined) {
            setError(LESSON_MESSAGES.completeFailed);
            return false;
        }

        inFlight.current = true;
        setCompleting(true);
        setError("");

        try {
            const data = await completeLessonAttempt(userId, attemptId, {
                course_id: courseId,
                content_id: contentId,
                lesson_attempt_id: attemptId,
                correct_answers: counters.current.correct,
                mistake_answers: counters.current.mistakes,
                hint_count: counters.current.hints,
                status: 1
            });

            setResult({
                xp: data.xp ?? 0,
                credit: data.credit ?? 0,
                streak: data.streak,
                streakChanged: Boolean(data.streak_changed)
            });

            // The header and the account sidebar show xp, credit and streak: bring them up to date
            void refreshUser();
            return true;
        } catch (err) {
            console.error("Completing failed:", err);
            setError(LESSON_MESSAGES.completeFailed);
            return false;
        } finally {
            inFlight.current = false;
            setCompleting(false);
        }
    }, [alreadyCompleted, userId, attemptId, courseId, contentId, refreshUser]);

    return { recordAnswer, recordHint, complete, completing, error, result };
};